/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';

import { EpisodeInfo } from 'client-code/episode-guide';
import { PlayableVodItem } from 'client-code/vod-browse';
import { setListEntryPosition } from 'src/store/list/list.actions';

import { DeviceModel, Settings } from '../../../core/device';
import { Locale, LocaleKeys } from '../../../core/locale';
import { ScreenControllerTypes, ScreenTaskControllerTypes } from '../../../core/navigation';
import { SharedPrefConstants, SharedPreferences } from '../../../core/storage';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { TListViewType } from '../../../core/types';
import { ListView } from '../../../core/usecases';
import { ListTypes } from '../../../core/utils';
import { TListViewScreen } from '../../../store/listscreenview';
import {
    resetSeasonPickersFocus,
    setSeasonPickerInFocus,
} from '../../../store/seasonpicker/seasonpicker.reducers';
import { ClockTypes } from '../../Clock';
import { List, ScreenHeader } from '../../common';
import { SeasonPicker } from '../../common/SeasonPicker';
import { VodPreview } from '../../common/VodPreview/VodPreview';
import { VodScreenType } from '../../common/VodPreview/VodPreviewProps';
import { useScreenController } from '../../hooks';
import AllEpisodesListItem from './AllEpisodesListItem';
import { AllEpisodesListProps } from './AllEpisodesListProps';
import { AllEpisodesListUtility, ListItem, SeasonGroup } from './AllEpisodesListUtility';

import './AllEpisodesList.scss';

const PREVIEW_DEBOUNCE_DELAY = 300; // ms

const AllEpisodesList: FunctionComponent<AllEpisodesListProps> = ({
    episodes,
    title,
    isLoading,
    ghostMode,
}): JSX.Element => {
    const dispatch = useTivoDispatch();

    const [flattenedList, setFlattenedList] = useState<ListItem[]>([]);
    const [focusedVodItem, setFocusedVodItem] = useState<PlayableVodItem>();
    const previewDebounceTimer = useRef<NodeJS.Timeout | null>(null);

    const listPosition = useTivoSelector(
        (state: RootState) => state.list[ListTypes.ALL_EPISODES_LIST]?.position,
    );
    const listIsFocused = useTivoSelector(
        (state: RootState) => state.list[ListTypes.ALL_EPISODES_LIST]?.isFocused,
    );
    const page = useTivoSelector((state: RootState) => state.listViewScreen.page);
    const ListViewScreenIns = useScreenController<TListViewScreen>({
        identifier: 'ListViewScreen',
        properties: {
            screenController: ScreenControllerTypes.LIST_VIEW_SCREEN,
            taskController: ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK,
        },
    });

    const id = ListViewScreenIns?.screen?.getProperties()?.id || '';

    const listViewService = useMemo(() => new ListView(), []);
    const isVideoWindowEnabled = useMemo(() => new Settings().isVideoWindowEnabled(), []);

    const computePickerItems = (
        seasonNumber: number,
    ): { listItemIndex: number; text: string }[] => {
        const computedSeasonData = listViewService.getComputedSeasonData(id);
        if (!computedSeasonData) {
            return [{ text: seasonNumber.toString(), listItemIndex: 0 }];
        }
        // Get all seasons
        const seasons = computedSeasonData.seasonGrouping;

        // Format seasons for picker items
        const pickerItems = seasons?.map((s, index) => ({
            listItemIndex: index,
            text: s === '-1' ? AllEpisodesListUtility.EXTRA_SEASON_TEXT_VALUE : s.toString(),
        })) || [{ text: seasonNumber.toString(), listItemIndex: 0 }];
        return pickerItems;
    };

    // Group episodes by season and create flattened list with headers
    useEffect(() => {
        if (!episodes?.length) {
            setFlattenedList([]);
            return;
        }

        const maxHeadersToShow =
            AllEpisodesListUtility.MAX_ITEM_IN_PAGE_ALL_EPISODES - episodes.length;
        const computedSeasonData = listViewService.getComputedSeasonData(id);

        const groupedSeasons = AllEpisodesListUtility.groupEpisodesBySeason(
            episodes,
            computedSeasonData,
            computePickerItems,
        );

        // Check if the first season on this page already had its header shown on a previous page
        const skipFirstHeader = AllEpisodesListUtility.isFirstSeasonContinuedFromPreviousPage(
            page,
            computedSeasonData?.seasonData?.seasons || [],
        );

        const flattenedListWithHeaders = AllEpisodesListUtility.buildFlattenedListWithHeaders(
            groupedSeasons,
            maxHeadersToShow,
            skipFirstHeader,
        );

        setFlattenedList(flattenedListWithHeaders);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [episodes]);

    useEffect(() => {
        const listType: TListViewType = 'allEpisodes';
        const allEpisodesListSharedPref = new SharedPreferences(
            SharedPrefConstants.LIST_SCREEN_PREF_KEY,
        ).edit();

        const selectedItems = {
            flattenedList,
            type: listType,
            id,
        };
        allEpisodesListSharedPref.putJson(
            SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION,
            selectedItems as unknown as any,
        );

        allEpisodesListSharedPref.apply();
    }, [flattenedList, id]);

    useEffect(() => {
        if (!flattenedList?.length) return;
        dispatch(resetSeasonPickersFocus());

        const sharedPreferences = DeviceModel.getSharedPreferences(
            SharedPrefConstants.LIST_SCREEN_PREF_KEY,
        );

        const listViewState = sharedPreferences.getJson(SharedPrefConstants.LISTVIEW_STATE) || {};

        const highlight =
            (id && listViewState[id]?.lastHighlight) ??
            ListViewScreenIns?.screen?.getProperties()?.highlight ??
            (flattenedList && flattenedList?.findIndex((item) => !('isHeader' in item)));

        const highlightedItem = flattenedList[highlight];
        let resolvedPosition = highlight;

        if (highlightedItem && AllEpisodesListUtility.isSeasonHeader(highlightedItem)) {
            if (highlightedItem.pickerType === 'picker') {
                dispatch(setSeasonPickerInFocus(highlightedItem.seasonTitle));
            } else {
                const firstEpisodeIndex = flattenedList.findIndex(
                    (item, idx) => idx > highlight && !AllEpisodesListUtility.isSeasonHeader(item),
                );
                resolvedPosition = firstEpisodeIndex >= 0 ? firstEpisodeIndex : resolvedPosition;
            }
        }
        dispatch(setListEntryPosition(ListTypes.ALL_EPISODES_LIST, resolvedPosition));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flattenedList]);

    useEffect(() => {
        // Clear any existing debounce timer
        if (previewDebounceTimer.current) {
            clearTimeout(previewDebounceTimer.current);
        }

        const currentPosition = listPosition ?? 0;
        const currentItem = flattenedList[currentPosition];

        // Debounce the preview update
        previewDebounceTimer.current = setTimeout(() => {
            // Only set focused item if it's not a season header and exists
            if (currentItem && !AllEpisodesListUtility.isSeasonHeader(currentItem)) {
                // Convert EpisodeInfo to PlayableVodItem with proper kernel structure
                const vodItem =
                    AllEpisodesListUtility.createPlayableVodItemFromEpisode(currentItem);
                setFocusedVodItem(vodItem);
            } else {
                setFocusedVodItem(undefined);
            }
        }, PREVIEW_DEBOUNCE_DELAY);

        return () => {
            if (previewDebounceTimer.current) {
                clearTimeout(previewDebounceTimer.current);
            }
            setFocusedVodItem(undefined);
        };
    }, [flattenedList, listPosition, listIsFocused]);

    const renderSeasonPicker = (season: SeasonGroup, index: number): JSX.Element => {
        const computedSeasonData = listViewService.getComputedSeasonData(id);
        const mainListPosition = listPosition;
        const isMainListFocused = listIsFocused;
        if (!computedSeasonData) {
            return (
                <SeasonPicker
                    id={season.seasonTitle}
                    type="header"
                    title={season.seasonTitle}
                    listItemIndex={index}
                    mainListPosition={mainListPosition}
                    isMainListFocused={isMainListFocused}
                    ghostMode={ghostMode}
                />
            );
        }

        if (computedSeasonData.pickerType === 'header')
            return (
                <SeasonPicker
                    id={season.seasonTitle}
                    type="header"
                    title={season.seasonTitle}
                    listItemIndex={index}
                    mainListPosition={mainListPosition}
                    isMainListFocused={isMainListFocused}
                    ghostMode={ghostMode}
                />
            );

        return (
            <SeasonPicker
                id={season.seasonTitle}
                type="picker"
                title={season.seasonTitle}
                items={season.pickerItems}
                listItemIndex={index}
                mainListPosition={mainListPosition}
                isMainListFocused={isMainListFocused}
                ghostMode={ghostMode}
            />
        );
    };

    const renderEpisodeItem = (
        episode: EpisodeInfo,
        itemClass: string,
        index: number,
    ): JSX.Element => {
        const isFocused = index === listPosition;

        return (
            <AllEpisodesListItem
                episode={episode}
                itemClass={itemClass}
                index={index}
                isFocused={isFocused}
                ghostMode={ghostMode}
            />
        );
    };

    const renderListItem = (item: ListItem, itemClass: string, index: number): JSX.Element =>
        AllEpisodesListUtility.isSeasonHeader(item)
            ? renderSeasonPicker(item, index)
            : renderEpisodeItem(item, itemClass, index);

    const headerTitle = `${Locale.getString(LocaleKeys.ALL_EPISODE_DETAIL)}: ${title}`;
    const emptyMessage = Locale.getString(LocaleKeys.EMPTY_LIST_TEXT);

    const memoizedPreview = useMemo(() => {
        if (!focusedVodItem) return null;
        return (
            <VodPreview
                screenType={VodScreenType.ON_DEMAND_EPISODES_SCREEN}
                item={focusedVodItem}
            />
        );
    }, [focusedVodItem]);

    return (
        <div className="ae-list-container">
            <ScreenHeader title={headerTitle} clockType={ClockTypes.DEFAULT_CLOCK} />
            <div className="ae-list-content">
                <div className="ae-list-wrapper">
                    {flattenedList && flattenedList.length > 0 && (
                        <List
                            type={ListTypes.ALL_EPISODES_LIST}
                            itemList={flattenedList}
                            generateItemView={(item, itemClass, index) =>
                                renderListItem(item, itemClass, index || 0)
                            }
                            initialFocusedIndex={
                                DeviceModel.getSharedPreferences(
                                    SharedPrefConstants.LIST_SCREEN_PREF_KEY,
                                ).getJson(SharedPrefConstants.LISTVIEW_STATE)?.[id]
                                    ?.lastHighlight ?? 1
                            }
                        />
                    )}
                    {!isLoading && episodes.length === 0 && (
                        <div className="ae-empty-list">{emptyMessage}</div>
                    )}
                </div>
            </div>
            <div className={`ae-preview-container ${isVideoWindowEnabled ? 'video-enabled' : ''}`}>
                {memoizedPreview}
            </div>
        </div>
    );
};

export default AllEpisodesList;
